import { CompanyDto } from '../../api/company/models/CompanyDto'
import { PersonDto, PersonLiteDto } from '../../api/person/models/PersonDto'
import { CandidateLiteDto } from '../../api/role/models/CandidateDto'
import { MapExtra } from '../../utils/MapExtra'
import { getProfilePicUrl } from '../../utils/personUtils'
import { SetExtra } from '../../utils/SetExtra'
import { getCandidateStatusInfo } from '../manageRole/candidateStatusHelper'
import {
    D3CompanyNode,
    D3Link,
    D3Node,
    D3PersonNode,
    D3SimulationManager,
    getLinkKey,
    isCompanyNode,
    isPersonNode,
} from './D3SimulationManager'
import colors from 'tailwindcss/colors'

export class SDManager {
    private sdCompanies: MapExtra<string, SDCompany> = new MapExtra()
    private sdCandidates: MapExtra<string, SDCandidatePerson> = new MapExtra()
    private sdBluePeople: MapExtra<string, SDBluePerson> = new MapExtra()
    private sdLinkProps: MapExtra<string, SDLinkProps> = new MapExtra()
    private selectedNodeId: string | null = null
    private selectedNodeType: NodeTypes | null = null
    private showEnabledOnly: boolean = false
    private personLiteCache: MapExtra<string, PersonLiteDto> = new MapExtra()
    private companyCache: MapExtra<string, CompanyDto> = new MapExtra()
    private companyGetter?: (companyId: string) => Promise<CompanyDto>
    private showStatusInfo: boolean = true

    // events
    private readonly onPersonSelectedListeners: Array<
        (person?: PersonDto, personType?: PersonType) => void
    > = []
    private readonly onPersonHoveredListeners: Array<
        (person?: PersonDto, personType?: PersonType) => void
    > = []
    private readonly onCompanyContextMenuListeners: Array<
        (companyId: string, companyName: string, event: MouseEvent) => void
    > = []

    public constructor(private readonly simManager: D3SimulationManager) {
        // bind "this" to the event handlers so they can access the SDManager
        this.onNodeClick = this.onNodeClick.bind(this)
        this.onNodeHover = this.onNodeHover.bind(this)
        this.onNodeUnhover = this.onNodeUnhover.bind(this)
        this.onNodeContextMenu = this.onNodeContextMenu.bind(this)
        this.onBackgroundClick = this.onBackgroundClick.bind(this)

        // set up event listeners from the simManager
        simManager.registerOnNodeClick(this.onNodeClick)
        simManager.registerOnNodeHover(this.onNodeHover)
        simManager.registerOnNodeUnhover(this.onNodeUnhover)
        simManager.registerOnNodeContextMenu(this.onNodeContextMenu)
        simManager.registerOnBackgroundClick(this.onBackgroundClick)
    }

    public setCaches(
        personLiteCache: MapExtra<string, PersonLiteDto>,
        companyCache: MapExtra<string, CompanyDto>,
        companyGetter?: (companyId: string) => Promise<CompanyDto>,
    ) {
        this.personLiteCache = personLiteCache
        this.companyCache = companyCache
        this.companyGetter = companyGetter
    }

    public reset() {
        this.sdCompanies.clear()
        this.sdCandidates.clear()
        this.sdBluePeople.clear()
        this.sdLinkProps.clear()
        this.selectedNodeId = null
        this.selectedNodeType = null
        this.showEnabledOnly = false

        this.simManager.updateData([], [])
    }

    public setShowEnabledOnly(showEnabledOnly: boolean) {
        this.showEnabledOnly = showEnabledOnly

        const disabledNodeIdsAndLinkKeys =
            this.getNodeIdsAndLinkKeysWithRenderMode(RenderModes.DISABLED)

        if (this.showEnabledOnly) {
            this.simManager.hidePersonNodes(
                disabledNodeIdsAndLinkKeys.personNodeIds,
            )
            this.simManager.hideCompanyNodes(
                disabledNodeIdsAndLinkKeys.companyNodeIds,
            )
            this.simManager.hideLinks(disabledNodeIdsAndLinkKeys.linkKeys)
        } else {
            this.simManager.showPersonNodes(
                disabledNodeIdsAndLinkKeys.personNodeIds,
            )
            this.simManager.showCompanyNodes(
                disabledNodeIdsAndLinkKeys.companyNodeIds,
            )
            this.simManager.showLinks(disabledNodeIdsAndLinkKeys.linkKeys)
        }
    }

    public setShowStatusInfo(show: boolean) {
        this.showStatusInfo = show
    }

    public hideCompanyNodes(companyIds: string[]) {
        if (companyIds.length === 0) {
            return
        }

        this.simManager.hideCompanyNodes(companyIds)

        for (const companyId of companyIds) {
            if (this.sdCompanies.has(companyId)) {
                this.sdCompanies.getOrThrow(companyId).renderMode =
                    RenderModes.HIDDEN
            }
        }
    }

    private hidePersonNodesByIds(personIds: string[]) {
        if (personIds.length === 0) {
            return
        }

        this.simManager.hidePersonNodes(personIds)

        for (const personId of personIds) {
            if (this.sdCandidates.has(personId)) {
                this.sdCandidates.getOrThrow(personId).renderMode =
                    RenderModes.HIDDEN
            }
            if (this.sdBluePeople.has(personId)) {
                this.sdBluePeople.getOrThrow(personId).renderMode =
                    RenderModes.HIDDEN
            }
        }
    }

    public hideCompanyConnections(companyId: string):
        | {
              personIds: string[]
              hiddenPersonIds: string[]
              linkKeys: string[]
          }
        | undefined {
        if (!this.sdCompanies.has(companyId)) {
            return
        }

        const sdCompany = this.sdCompanies.getOrThrow(companyId)
        if (sdCompany.renderMode === RenderModes.HIDDEN) {
            return
        }

        const { nodeIds, linkKeys } =
            this.getLinkKeysAndNodeIdsForCompany(sdCompany)

        this.hideCompanyNodes([companyId])

        const personIds = nodeIds.filter(
            (id) => id !== companyId && id !== undefined,
        )
        const personIdsToHide = personIds.filter(
            (personId) => !this.hasVisibleCompanyConnection(personId),
        )
        this.hidePersonNodesByIds(personIdsToHide)

        this.hideLinks(linkKeys)

        return {
            personIds,
            hiddenPersonIds: personIdsToHide,
            linkKeys,
        }
    }

    private hasVisibleCompanyConnection(personId: string): boolean {
        if (this.sdCandidates.has(personId)) {
            const sdCandidate = this.sdCandidates.getOrThrow(personId)
            return sdCandidate.connections.some((conn) => {
                const company = this.sdCompanies.get(conn.parentCompanyId)
                return company && company.renderMode !== RenderModes.HIDDEN
            })
        }

        if (this.sdBluePeople.has(personId)) {
            for (const company of this.sdCompanies.getValuesAsArray()) {
                if (
                    company.renderMode !== RenderModes.HIDDEN &&
                    company.parentPersonIds.has(personId)
                ) {
                    return true
                }
            }
            return false
        }

        // if we don't recognize the person, leave them visible
        return true
    }

    public hideLinks(linkKeys: string[]) {
        if (linkKeys.length === 0) {
            return
        }

        this.simManager.hideLinks(linkKeys)

        for (const linkKey of linkKeys) {
            if (this.sdLinkProps.has(linkKey)) {
                this.sdLinkProps.getOrThrow(linkKey).renderMode =
                    RenderModes.HIDDEN
            }
        }
    }

    public showCompanyNodes(companyIds: string[]) {
        if (companyIds.length === 0) {
            return
        }

        this.simManager.showCompanyNodes(companyIds)
        for (const companyId of companyIds) {
            if (this.sdCompanies.has(companyId)) {
                this.sdCompanies.getOrThrow(companyId).renderMode =
                    RenderModes.NORMAL
            }
        }
    }

    public showPersonNodes(personIds: string[]) {
        if (personIds.length === 0) {
            return
        }

        this.simManager.showPersonNodes(personIds)
        for (const personId of personIds) {
            if (this.sdCandidates.has(personId)) {
                this.sdCandidates.getOrThrow(personId).renderMode =
                    RenderModes.NORMAL
            }
            if (this.sdBluePeople.has(personId)) {
                this.sdBluePeople.getOrThrow(personId).renderMode =
                    RenderModes.NORMAL
            }
        }
    }

    public showLinks(linkKeys: string[]) {
        if (linkKeys.length === 0) {
            return
        }

        this.simManager.showLinks(linkKeys)
        for (const linkKey of linkKeys) {
            if (this.sdLinkProps.has(linkKey)) {
                this.sdLinkProps.getOrThrow(linkKey).renderMode =
                    RenderModes.NORMAL
            }
        }
    }

    public revealAllHiddenNodesAndLinks() {
        const hidden = this.getNodeIdsAndLinkKeysWithRenderMode(
            RenderModes.HIDDEN,
        )

        if (hidden.personNodeIds.length > 0) {
            this.simManager.showPersonNodes(hidden.personNodeIds)
            this.changePersonNodesRenderMode(
                hidden.personNodeIds,
                RenderModes.NORMAL,
            )
        }

        if (hidden.companyNodeIds.length > 0) {
            this.simManager.showCompanyNodes(hidden.companyNodeIds)
            this.changeCompanyNodesRenderMode(
                hidden.companyNodeIds,
                RenderModes.NORMAL,
            )
        }

        if (hidden.linkKeys.length > 0) {
            this.simManager.showLinks(hidden.linkKeys)
            for (const linkKey of hidden.linkKeys) {
                if (this.sdLinkProps.has(linkKey)) {
                    this.sdLinkProps.getOrThrow(linkKey).renderMode =
                        RenderModes.NORMAL
                }
            }
        }
    }

    private async getCompany(companyId: string): Promise<CompanyDto> {
        if (this.companyCache.has(companyId)) {
            return this.companyCache.getOrThrow(companyId)
        } else if (this.companyGetter) {
            const company = await this.companyGetter(companyId)
            this.companyCache.set(companyId, company)
            return company
        } else {
            throw new Error(`Company with id ${companyId} not found`)
        }
    }

    private getNodeIdsAndLinkKeysWithRenderMode(renderMode: RenderModes): {
        linkKeys: string[]
        personNodeIds: string[]
        companyNodeIds: string[]
    } {
        const personNodeIds: string[] = []

        for (const candidate of this.sdCandidates.getValuesAsArray()) {
            if (candidate.renderMode === renderMode) {
                personNodeIds.push(candidate.candidate.candidatePersonId)
            }
        }

        for (const sdBluePerson of this.sdBluePeople.getValuesAsArray()) {
            if (sdBluePerson.renderMode === renderMode) {
                personNodeIds.push(sdBluePerson.person.id)
            }
        }

        const companyNodeIds: string[] = []

        for (const company of this.sdCompanies.getValuesAsArray()) {
            if (company.renderMode === renderMode) {
                companyNodeIds.push(company.company.id)
            }
        }

        const linkKeys: string[] = []

        for (const linkProp of this.sdLinkProps.getValuesAsArray()) {
            if (linkProp.renderMode === renderMode) {
                linkKeys.push(linkProp.key)
            }
        }

        return {
            linkKeys,
            personNodeIds,
            companyNodeIds,
        }
    }

    // d3 event listeners
    private onBackgroundClick(): void {
        this.resetSelectionAndEnableAll()
    }

    private resetSelectionAndEnableAll() {
        // if something is selected, deselect it
        this.deselectCurrent()

        // enable everything
        const allNodeIds = this.getAllNodeIds()
        this.changePersonNodesRenderMode(allNodeIds, RenderModes.NORMAL)
        this.changeCompanyNodesRenderMode(allNodeIds, RenderModes.NORMAL)
        this.changeLinksRenderMode(
            this.sdLinkProps.getKeysAsArray(),
            RenderModes.NORMAL,
        )
    }

    private onNodeClick(node: D3Node): void {
        if (isPersonNode(node)) {
            if (node.personType === PersonType.CANDIDATE) {
                this.onCandidatePersonClick(node)
            } else {
                this.onSeedPersonClick(node)
            }
        } else {
            this.onCompanyClick(node)
        }
    }

    private onNodeContextMenu(node: D3Node, event: MouseEvent): void {
        if (!isCompanyNode(node)) {
            return
        }

        for (const listener of this.onCompanyContextMenuListeners) {
            listener(node.id, node.name ?? '', event)
        }
    }

    private onSeedPersonClick(node: D3PersonNode) {
        const sdBluePerson = this.sdBluePeople.getOrThrow(node.id)

        this.deselectCurrent()

        const highlightedIds = this.highlightBluePerson(sdBluePerson)

        // disable everything else
        const linkKeysToDisable = this.getAllLinkKeys(
            highlightedIds.linkIdsHighlighted,
        )
        const nodeIdsToDisable = this.getAllNodeIds(
            highlightedIds.nodeIdsHighlighted,
        )

        this.changePersonNodesRenderMode(nodeIdsToDisable, RenderModes.DISABLED)
        this.changeCompanyNodesRenderMode(
            nodeIdsToDisable,
            RenderModes.DISABLED,
        )
        this.changeLinksRenderMode(linkKeysToDisable, RenderModes.DISABLED)

        // the node is officially "selected" at this point
        sdBluePerson.selected = true
        this.selectedNodeId = sdBluePerson.id
        this.selectedNodeType = NodeTypes.PERSON_BLUE
        this.triggerOnPersonSelected(
            sdBluePerson.person,
            sdBluePerson.personType,
        )
    }

    private onNodeHover(node: D3Node): void {
        if (this.selectedNodeId) {
            // if something is already selected we don't want to highlight/unhighlight things on hover
            return
        }

        if (isPersonNode(node)) {
            if (node.personType === PersonType.CANDIDATE) {
                const sdCandidate = this.sdCandidates.getOrThrow(node.id)
                // we don't want to fully select them, but we do want to highlight their network, without disabling everything else
                this.highlightCandidate(sdCandidate)
                this.triggerOnPersonHovered(
                    this.personLiteCache.getOrThrow(
                        sdCandidate.candidate.candidatePersonId,
                    ),
                    PersonType.CANDIDATE,
                )
            } else {
                const sdPerson = this.sdBluePeople.getOrThrow(node.id)

                this.highlightBluePerson(sdPerson)
                this.triggerOnPersonHovered(
                    sdPerson.person,
                    sdPerson.personType,
                )
            }
        } else {
            const sdCompany = this.sdCompanies.getOrThrow(node.id)
            this.highlightCompany(sdCompany)
            // TODO: raise company hovered event so the right pane can show the company details
        }
    }

    private onNodeUnhover(node: D3Node): void {
        if (this.selectedNodeId) {
            // if something is already selected we don't want to highlight/unhighlight things on hover
            return
        }

        if (isPersonNode(node)) {
            if (node.personType === PersonType.CANDIDATE) {
                // candidate
                this.unhighlightCandidate(this.sdCandidates.getOrThrow(node.id))
                this.triggerOnPersonHovered()
            } else {
                this.unhighlightBluePerson(
                    this.sdBluePeople.getOrThrow(node.id),
                )
                this.triggerOnPersonHovered()
            }
        } else {
            // company
            this.unhighlightCompany(this.sdCompanies.getOrThrow(node.id))
        }
    }

    private highlightCandidate(sdCandidate: SDCandidatePerson): {
        nodeIdsHighlighted: string[]
        linkIdsHighlighted: string[]
    } {
        const nodeIdsToHighlight: string[] = []
        nodeIdsToHighlight.push(sdCandidate.candidate.candidatePersonId)

        const linkKeysToHighlight: string[] =
            this.getLinkKeysForCandidate(sdCandidate)

        // highlight the links to the companies where we have a connection to the seed person
        for (const conn of sdCandidate.connections) {
            nodeIdsToHighlight.push(conn.parentPersonId)
            nodeIdsToHighlight.push(conn.parentCompanyId)
        }

        // nodes
        this.changePersonNodesRenderMode(
            nodeIdsToHighlight,
            RenderModes.HIGHLIGHTED,
        )
        this.changeCompanyNodesRenderMode(
            nodeIdsToHighlight,
            RenderModes.HIGHLIGHTED,
        )

        // links
        this.changeLinksRenderMode(linkKeysToHighlight, RenderModes.HIGHLIGHTED)

        return {
            nodeIdsHighlighted: nodeIdsToHighlight,
            linkIdsHighlighted: linkKeysToHighlight,
        }
    }

    private highlightBluePerson(sdBluePerson: SDBluePerson): {
        nodeIdsHighlighted: string[]
        linkIdsHighlighted: string[]
    } {
        const linkKeysAndNodeIdsToHighlight =
            this.getLinkKeysAndNodeIdsForBluePerson(sdBluePerson)

        // nodes
        this.changePersonNodesRenderMode(
            linkKeysAndNodeIdsToHighlight.nodeIds,
            RenderModes.HIGHLIGHTED,
        )
        this.changeCompanyNodesRenderMode(
            linkKeysAndNodeIdsToHighlight.nodeIds,
            RenderModes.HIGHLIGHTED,
        )

        // links
        this.changeLinksRenderMode(
            linkKeysAndNodeIdsToHighlight.linkKeys,
            RenderModes.HIGHLIGHTED,
        )

        return {
            nodeIdsHighlighted: linkKeysAndNodeIdsToHighlight.nodeIds,
            linkIdsHighlighted: linkKeysAndNodeIdsToHighlight.linkKeys,
        }
    }

    private getLinkKeysAndNodeIdsForBluePerson(sdBluePerson: SDBluePerson): {
        linkKeys: string[]
        nodeIds: string[]
    } {
        const linkKeys = new SetExtra<string>()
        const nodeIds = new SetExtra<string>()

        // find all candidates that we have a connection to
        const linkedCandidates = this.sdCandidates
            .getValuesAsArray()
            .filter((c) => {
                return c.connections.some((conn) => {
                    return conn.parentPersonId === sdBluePerson.id
                })
            })

        // now create link keys between the blue person and all the companies
        //  and all the companies and the candidates
        for (const candidate of linkedCandidates) {
            for (const conn of candidate.connections) {
                if (conn.parentPersonId === sdBluePerson.id) {
                    // link between the candidate and the company
                    linkKeys.add(
                        getLinkKey(
                            conn.parentCompanyId,
                            candidate.candidate.candidatePersonId,
                        ),
                    )

                    // link between the company and the seed person
                    linkKeys.add(
                        getLinkKey(sdBluePerson.id, conn.parentCompanyId),
                    )

                    // add the company, candidate and seed person nodes
                    nodeIds.add(conn.parentPersonId)
                    nodeIds.add(conn.parentCompanyId)
                    nodeIds.add(candidate.candidate.candidatePersonId)
                }
            }
        }

        return {
            linkKeys: linkKeys.getValuesAsArray(),
            nodeIds: nodeIds.getValuesAsArray(),
        }
    }

    private highlightCompany(sdCompany: SDCompany): {
        nodeIdsHighlighted: string[]
        linkIdsHighlighted: string[]
    } {
        const linkKeysAndNodeIdsToHighlight =
            this.getLinkKeysAndNodeIdsForCompany(sdCompany)

        // nodes
        this.changePersonNodesRenderMode(
            linkKeysAndNodeIdsToHighlight.nodeIds,
            RenderModes.HIGHLIGHTED,
        )
        this.changeCompanyNodesRenderMode(
            linkKeysAndNodeIdsToHighlight.nodeIds,
            RenderModes.HIGHLIGHTED,
        )

        // links
        this.changeLinksRenderMode(
            linkKeysAndNodeIdsToHighlight.linkKeys,
            RenderModes.HIGHLIGHTED,
        )

        return {
            nodeIdsHighlighted: linkKeysAndNodeIdsToHighlight.nodeIds,
            linkIdsHighlighted: linkKeysAndNodeIdsToHighlight.linkKeys,
        }
    }

    private getLinkKeysAndNodeIdsForCompany(sdCompany: SDCompany): {
        linkKeys: string[]
        nodeIds: string[]
    } {
        const linkKeys = new SetExtra<string>()
        const nodeIds = new SetExtra<string>()

        nodeIds.add(sdCompany.id)

        // find all the candidates connected to the company: company -> candidate
        // find all the seed people connected to the company: seed person -> company

        for (const candidate of this.sdCandidates.getValuesAsArray()) {
            for (const conn of candidate.connections) {
                if (conn.parentCompanyId === sdCompany.id) {
                    // add the seed person and the candidate
                    nodeIds.add(conn.parentPersonId)
                    nodeIds.add(candidate.id)

                    // add the link between the seed person and the company
                    linkKeys.add(getLinkKey(conn.parentPersonId, sdCompany.id))
                    // add the link between the company and the candidate
                    linkKeys.add(getLinkKey(sdCompany.id, candidate.id))
                }
            }
        }

        return {
            linkKeys: linkKeys.getValuesAsArray(),
            nodeIds: nodeIds.getValuesAsArray(),
        }
    }

    public getLinkKeysForCompany(companyId: string): string[] {
        if (!this.sdCompanies.has(companyId)) {
            return []
        }

        return this.getLinkKeysAndNodeIdsForCompany(
            this.sdCompanies.getOrThrow(companyId),
        ).linkKeys
    }

    private unhighlightCandidate(sdCandidate: SDCandidatePerson) {
        const nodeIdsToUnhighlight: string[] = []
        nodeIdsToUnhighlight.push(sdCandidate.candidate.candidatePersonId)

        const linkKeysToUnhighlight: string[] =
            this.getLinkKeysForCandidate(sdCandidate)

        for (const conn of sdCandidate.connections) {
            nodeIdsToUnhighlight.push(conn.parentPersonId)
            nodeIdsToUnhighlight.push(conn.parentCompanyId)
        }

        this.changePersonNodesRenderMode(
            nodeIdsToUnhighlight,
            RenderModes.NORMAL,
        )
        this.changeCompanyNodesRenderMode(
            nodeIdsToUnhighlight,
            RenderModes.NORMAL,
        )
        this.changeLinksRenderMode(linkKeysToUnhighlight, RenderModes.NORMAL)
    }

    private unhighlightBluePerson(sdBluePerson: SDBluePerson) {
        const idsToUnhighlight =
            this.getLinkKeysAndNodeIdsForBluePerson(sdBluePerson)

        this.changePersonNodesRenderMode(
            idsToUnhighlight.nodeIds,
            RenderModes.NORMAL,
        )
        this.changeCompanyNodesRenderMode(
            idsToUnhighlight.nodeIds,
            RenderModes.NORMAL,
        )
        this.changeLinksRenderMode(
            idsToUnhighlight.linkKeys,
            RenderModes.NORMAL,
        )
    }

    private unhighlightCompany(sdCompany: SDCompany) {
        const idsToUnhighlight = this.getLinkKeysAndNodeIdsForCompany(sdCompany)

        this.changePersonNodesRenderMode(
            idsToUnhighlight.nodeIds,
            RenderModes.NORMAL,
        )
        this.changeCompanyNodesRenderMode(
            idsToUnhighlight.nodeIds,
            RenderModes.NORMAL,
        )
        this.changeLinksRenderMode(
            idsToUnhighlight.linkKeys,
            RenderModes.NORMAL,
        )
    }

    private deselectCurrent() {
        // could be a seed person, candidate, or no one selected
        if (!this.selectedNodeId) {
            // nothing selected, do nothing
            return
        }

        switch (this.selectedNodeType) {
            case NodeTypes.PERSON_BLUE:
                this.deselectBluePerson(
                    this.sdBluePeople.getOrThrow(this.selectedNodeId),
                )
                break
            case NodeTypes.PERSON_CANDIDATE:
                this.deselectCandidate(
                    this.sdCandidates.getOrThrow(this.selectedNodeId),
                )
                break
            case NodeTypes.COMPANY:
                this.deselectCompany(
                    this.sdCompanies.getOrThrow(this.selectedNodeId),
                )
                break
            default:
                throw new Error(
                    `Unknown selected node type: ${this.selectedNodeType}`,
                )
        }
    }

    // "selecting" can candidate is a high level function that covers a lot of changes in
    //  how things are rendered
    private selectCandidate(sdCandidate: SDCandidatePerson) {
        this.deselectCurrent()

        const highlightedNodeIds = this.highlightCandidate(sdCandidate)

        // disable everything else
        const linkKeysToDisable = this.getAllLinkKeys(
            highlightedNodeIds.linkIdsHighlighted,
        )
        const nodeIdsToDisable = this.getAllNodeIds(
            highlightedNodeIds.nodeIdsHighlighted,
        )

        this.changePersonNodesRenderMode(nodeIdsToDisable, RenderModes.DISABLED)
        this.changeCompanyNodesRenderMode(
            nodeIdsToDisable,
            RenderModes.DISABLED,
        )
        this.changeLinksRenderMode(linkKeysToDisable, RenderModes.DISABLED)

        // the node is officially "selected" at this point
        sdCandidate.selected = true
        this.selectedNodeId = sdCandidate.candidate.candidatePersonId
        this.selectedNodeType = NodeTypes.PERSON_CANDIDATE
        this.triggerOnPersonSelected(
            this.personLiteCache.getOrThrow(
                sdCandidate.candidate.candidatePersonId,
            ),
            PersonType.CANDIDATE,
        )
    }

    // unhighlights and disables the selected candidate
    private deselectCandidate(sdCandidate: SDCandidatePerson) {
        this.unhighlightCandidate(sdCandidate)

        // the node is officially "UNselected" at this point
        sdCandidate.selected = false
        this.selectedNodeId = null
        this.selectedNodeType = null
        this.triggerOnPersonSelected()
    }

    private deselectBluePerson(sdBluePerson: SDBluePerson) {
        this.unhighlightBluePerson(sdBluePerson)

        // the node is officially "UNselected" at this point
        sdBluePerson.selected = false
        this.selectedNodeId = null
        this.selectedNodeType = null
        this.triggerOnPersonSelected()
    }

    private deselectCompany(sdCompany: SDCompany) {
        this.unhighlightCompany(sdCompany)

        // the node is officially "UNselected" at this point
        sdCompany.selected = false
        this.selectedNodeId = null
        this.selectedNodeType = null
        // TODO: company events
        // this.triggerOnPersonSelected()
    }

    /**
     * we carefully figure out which nodes need various rendering processes executed against them
     *  the idea is that we don't just blanket apply a render mode to lots of nodes if they don't need it
     *  The reason for this is to avoid too much processing and to avoid flickering on the screen
     */
    private changePersonNodesRenderMode(
        nodeIds: string[],
        newRenderMode: RenderModes,
    ) {
        const personNodes = this.getPersonNodes(nodeIds)

        const nodeIdsToAction = this.getNodeActionsFromRenderModeChange(
            personNodes,
            newRenderMode,
        )

        if (nodeIdsToAction.nodeIdsToEnable.length > 0) {
            this.simManager.enablePersonNodes(nodeIdsToAction.nodeIdsToEnable)
        }
        if (nodeIdsToAction.nodeIdsToDisable.length > 0) {
            this.simManager.disablePersonNodes(nodeIdsToAction.nodeIdsToDisable)
        }
        if (nodeIdsToAction.nodeIdsToHighlight.length > 0) {
            this.simManager.highlightPersonNodes(
                nodeIdsToAction.nodeIdsToHighlight,
            )
        }
        if (nodeIdsToAction.nodeIdsToUnhighlight.length > 0) {
            this.simManager.unhighlightPersonNodes(
                nodeIdsToAction.nodeIdsToUnhighlight,
            )
        }

        // now update all the nodes to have the latest render mode
        for (const node of personNodes) {
            node.renderMode = newRenderMode
        }
    }

    private getNodeActionsFromRenderModeChange(
        nodes: SDNodeBase[],
        newRenderMode: RenderModes,
    ): {
        nodeIdsToUnhighlight: string[]
        nodeIdsToHighlight: string[]
        nodeIdsToDisable: string[]
        nodeIdsToEnable: string[]
    } {
        const nodeIdsToUnhighlight: string[] = []
        const nodeIdsToHighlight: string[] = []
        const nodeIdsToDisable: string[] = []
        const nodeIdsToEnable: string[] = []

        for (const node of nodes) {
            switch (node.renderMode) {
                case RenderModes.NORMAL:
                    switch (newRenderMode) {
                        case RenderModes.NORMAL:
                            // do nothing - already in this state
                            break
                        case RenderModes.DISABLED:
                            nodeIdsToDisable.push(node.id)
                            break
                        case RenderModes.HIGHLIGHTED:
                            nodeIdsToHighlight.push(node.id)
                            break
                        case RenderModes.HIDDEN:
                            // special case where we have to actually remove the nodes from the D3 diagram
                            throw new Error(
                                `Not implemented yet newRenderMode: ${newRenderMode}`,
                            )
                        default:
                            throw new Error(
                                `Unknown render mode: ${newRenderMode}`,
                            )
                    }
                    break
                case RenderModes.DISABLED:
                    switch (newRenderMode) {
                        case RenderModes.NORMAL:
                            // then enable
                            nodeIdsToEnable.push(node.id)
                            break
                        case RenderModes.DISABLED:
                            // do nothing - already in this state
                            break
                        case RenderModes.HIGHLIGHTED:
                            // a node has to be enabled in order to highlight it, so enable first
                            nodeIdsToEnable.push(node.id)
                            // then highlight
                            nodeIdsToHighlight.push(node.id)
                            break
                        case RenderModes.HIDDEN:
                            // special case where we have to actually remove the nodes from the D3 diagram
                            throw new Error(
                                `Not implemented yet newRenderMode: ${newRenderMode}`,
                            )
                        default:
                            throw new Error(
                                `Unknown render mode: ${newRenderMode}`,
                            )
                    }
                    break
                case RenderModes.HIGHLIGHTED:
                    switch (newRenderMode) {
                        case RenderModes.NORMAL:
                            // remove highlight stuff
                            nodeIdsToUnhighlight.push(node.id)
                            break
                        case RenderModes.DISABLED:
                            // remove highlight stuff
                            nodeIdsToUnhighlight.push(node.id)
                            // then disable
                            nodeIdsToDisable.push(node.id)
                            break
                        case RenderModes.HIGHLIGHTED:
                            // do nothing - already in this state
                            break
                        case RenderModes.HIDDEN:
                            // special case where we have to actually remove the nodes from the D3 diagram
                            throw new Error(
                                `Not implemented yet newRenderMode: ${newRenderMode}`,
                            )
                        default:
                            throw new Error(
                                `Unknown render mode: ${newRenderMode}`,
                            )
                    }
                    break
                case RenderModes.HIDDEN:
                    switch (newRenderMode) {
                        case RenderModes.NORMAL:
                            nodeIdsToEnable.push(node.id)
                            break
                        case RenderModes.HIDDEN:
                            break
                        default:
                            throw new Error(
                                `Not implemented yet newRenderMode: ${newRenderMode}`,
                            )
                    }
                    break
                default:
                    throw new Error(`Unknown render mode: ${newRenderMode}`)
            }
        }

        return {
            nodeIdsToDisable,
            nodeIdsToEnable,
            nodeIdsToHighlight,
            nodeIdsToUnhighlight,
        }
    }

    private changeCompanyNodesRenderMode(
        nodeIds: string[],
        newRenderMode: RenderModes,
    ) {
        const companyNodes = this.getCompanyNodes(nodeIds)

        const nodeIdsToAction = this.getNodeActionsFromRenderModeChange(
            companyNodes,
            newRenderMode,
        )

        if (nodeIdsToAction.nodeIdsToEnable.length > 0) {
            this.simManager.enableCompanyNodes(nodeIdsToAction.nodeIdsToEnable)
        }
        if (nodeIdsToAction.nodeIdsToDisable.length > 0) {
            this.simManager.disableCompanyNodes(
                nodeIdsToAction.nodeIdsToDisable,
            )
        }
        if (nodeIdsToAction.nodeIdsToHighlight.length > 0) {
            this.simManager.highlightCompanyNodes(
                nodeIdsToAction.nodeIdsToHighlight,
            )
        }
        if (nodeIdsToAction.nodeIdsToUnhighlight.length > 0) {
            this.simManager.unhighlightCompanyNodes(
                nodeIdsToAction.nodeIdsToUnhighlight,
            )
        }

        // now update all the nodes to have the latest render mode
        for (const node of companyNodes) {
            node.renderMode = newRenderMode
        }
    }

    private changeLinksRenderMode(
        linkKeys: string[],
        newRenderMode: RenderModes,
    ) {
        const linkProps = this.sdLinkProps
            .getValuesAsArray()
            .filter((linkProp) =>
                linkKeys.some((key) => {
                    return key.toString() === linkProp.key.toString()
                }),
            )

        const linkKeysToUnhighlight: string[] = []
        const linkKeysToHighlight: string[] = []
        const linkKeysToDisable: string[] = []
        const linkKeysToEnable: string[] = []

        for (const linkProp of linkProps) {
            switch (linkProp.renderMode) {
                case RenderModes.NORMAL:
                    switch (newRenderMode) {
                        case RenderModes.NORMAL:
                            // do nothing - already in this state
                            break
                        case RenderModes.DISABLED:
                            linkKeysToDisable.push(linkProp.key)
                            break
                        case RenderModes.HIGHLIGHTED:
                            linkKeysToHighlight.push(linkProp.key)
                            break
                        case RenderModes.HIDDEN:
                            // special case where we have to actually remove the nodes from the D3 diagram
                            throw new Error(
                                `Not implemented yet newRenderMode: ${newRenderMode}`,
                            )
                        default:
                            throw new Error(
                                `Unknown render mode: ${newRenderMode}`,
                            )
                    }
                    break
                case RenderModes.DISABLED:
                    switch (newRenderMode) {
                        case RenderModes.NORMAL:
                            // then enable
                            linkKeysToEnable.push(linkProp.key)
                            break
                        case RenderModes.DISABLED:
                            // do nothing - already in this state
                            break
                        case RenderModes.HIGHLIGHTED:
                            // a node has to be enabled in order to highlight it, so enable first
                            linkKeysToEnable.push(linkProp.key)
                            // then highlight
                            linkKeysToHighlight.push(linkProp.key)
                            break
                        case RenderModes.HIDDEN:
                            // special case where we have to actually remove the nodes from the D3 diagram
                            throw new Error(
                                `Not implemented yet newRenderMode: ${newRenderMode}`,
                            )
                        default:
                            throw new Error(
                                `Unknown render mode: ${newRenderMode}`,
                            )
                    }
                    break
                case RenderModes.HIGHLIGHTED:
                    switch (newRenderMode) {
                        case RenderModes.NORMAL:
                            // remove highlight stuff
                            linkKeysToUnhighlight.push(linkProp.key)
                            break
                        case RenderModes.DISABLED:
                            // remove highlight stuff
                            linkKeysToUnhighlight.push(linkProp.key)
                            // then disable
                            linkKeysToDisable.push(linkProp.key)
                            break
                        case RenderModes.HIGHLIGHTED:
                            // do nothing - already in this state
                            break
                        case RenderModes.HIDDEN:
                            // special case where we have to actually remove the nodes from the D3 diagram
                            throw new Error(
                                `Not implemented yet newRenderMode: ${newRenderMode}`,
                            )
                        default:
                            throw new Error(
                                `Unknown render mode: ${newRenderMode}`,
                            )
                    }
                    break
                case RenderModes.HIDDEN:
                    switch (newRenderMode) {
                        case RenderModes.NORMAL:
                            linkKeysToEnable.push(linkProp.key)
                            break
                        case RenderModes.HIDDEN:
                            break
                        default:
                            throw new Error(
                                `Not implemented yet newRenderMode: ${newRenderMode}`,
                            )
                    }
                    break
                default:
                    throw new Error(`Unknown render mode: ${newRenderMode}`)
            }
        }

        if (linkKeysToEnable.length > 0) {
            this.simManager.enableLinks(linkKeysToEnable)
        }
        if (linkKeysToDisable.length > 0) {
            this.simManager.disableLinks(linkKeysToDisable)
        }
        if (linkKeysToHighlight.length > 0) {
            this.simManager.highlightLinks(linkKeysToHighlight)
        }
        if (linkKeysToUnhighlight.length > 0) {
            this.simManager.unhighlightLinks(linkKeysToUnhighlight)
        }

        // now update all the nodes to have the latest render mode
        for (const linkProp of linkProps) {
            linkProp.renderMode = newRenderMode
        }
    }

    /**
     * Get the nodes from all of the person node maps - blue, green, candidate
     */
    private getPersonNodes(nodeIds: string[]): SDNodeBase[] {
        const nodes: SDNodeBase[] = []

        for (const nodeId of nodeIds) {
            if (this.sdBluePeople.has(nodeId)) {
                nodes.push(this.sdBluePeople.getOrThrow(nodeId))
            } else if (this.sdCandidates.has(nodeId)) {
                nodes.push(this.sdCandidates.getOrThrow(nodeId))
            }
            // else, do nothing, must be a company node id
        }

        return nodes
    }

    private getCompanyNodes(nodeIds: string[]): SDNodeBase[] {
        const nodes: SDNodeBase[] = []

        for (const nodeId of nodeIds) {
            if (this.sdCompanies.has(nodeId)) {
                nodes.push(this.sdCompanies.getOrThrow(nodeId))
            }
            // else, do nothing, must be a person node id
        }

        return nodes
    }

    private getLinkKeysForCandidate(candidate: SDCandidatePerson): string[] {
        const linkKeys: string[] = []

        for (const conn of candidate.connections) {
            // link between the candidate and the company
            linkKeys.push(
                getLinkKey(
                    conn.parentCompanyId,
                    candidate.candidate.candidatePersonId,
                ),
            )

            // link between the company and the seed person
            linkKeys.push(getLinkKey(conn.parentPersonId, conn.parentCompanyId))
        }

        return linkKeys
    }

    private onCandidatePersonClick(node: D3PersonNode) {
        const sdCandidate = this.sdCandidates.getOrThrow(node.id)
        this.selectCandidate(sdCandidate)
    }

    private onCompanyClick(node: D3CompanyNode) {
        const sdCompany = this.sdCompanies.getOrThrow(node.id)

        this.deselectCurrent()

        const highlightedIds = this.highlightCompany(sdCompany)

        // disable everything else
        const linkKeysToDisable = this.getAllLinkKeys(
            highlightedIds.linkIdsHighlighted,
        )
        const nodeIdsToDisable = this.getAllNodeIds(
            highlightedIds.nodeIdsHighlighted,
        )

        this.changePersonNodesRenderMode(nodeIdsToDisable, RenderModes.DISABLED)
        this.changeCompanyNodesRenderMode(
            nodeIdsToDisable,
            RenderModes.DISABLED,
        )
        this.changeLinksRenderMode(linkKeysToDisable, RenderModes.DISABLED)

        // the node is officially "selected" at this point
        sdCompany.selected = true
        this.selectedNodeId = sdCompany.id
        this.selectedNodeType = NodeTypes.COMPANY
        // TODO: trigger company select event so right pane can show the details
        // this.triggerOnPersonSelected(sdCompany.person)
    }

    private getAllNodeIds(excludeNodeIds: string[] = []): string[] {
        if (excludeNodeIds.length === 0) {
            return [
                ...this.sdBluePeople.getKeysAsArray(),
                ...this.sdCandidates.getKeysAsArray(),
                ...this.sdCompanies.getKeysAsArray(),
            ]
        }

        const allNodeIds: string[] = []

        this.sdBluePeople.forEach((bluePerson) => {
            if (!excludeNodeIds.includes(bluePerson.person.id)) {
                allNodeIds.push(bluePerson.person.id)
            }
        })

        this.sdCandidates.forEach((candidate) => {
            if (
                !excludeNodeIds.includes(candidate.candidate.candidatePersonId)
            ) {
                allNodeIds.push(candidate.candidate.candidatePersonId)
            }
        })

        this.sdCompanies.forEach((company) => {
            if (!excludeNodeIds.includes(company.company.id)) {
                allNodeIds.push(company.company.id)
            }
        })

        return allNodeIds
    }

    private getAllLinkKeys(excludeLinkKeys: string[] = []): string[] {
        if (excludeLinkKeys.length === 0) {
            return this.sdLinkProps.getKeysAsArray()
        }

        return this.sdLinkProps.getKeysAsArray().filter((linkKey) => {
            return !excludeLinkKeys.some(
                (excludeLinkKey) => excludeLinkKey === linkKey,
            )
        })
    }

    // private renderDeselectAll() {
    //     this.simManager.updateLinkThicknessAll(2)
    //     this.simManager.enableAllPersonNodes()
    // }

    // events raised from this sd manager
    public registerOnPersonSelected(
        cb: (person?: PersonDto, personType?: PersonType) => void,
    ): void {
        this.onPersonSelectedListeners.push(cb)
    }

    public registerOnPersonHovered(
        cb: (person?: PersonDto, personType?: PersonType) => void,
    ): void {
        this.onPersonHoveredListeners.push(cb)
    }
    public registerOnCompanyContextMenu(
        cb: (companyId: string, companyName: string, event: MouseEvent) => void,
    ): void {
        this.onCompanyContextMenuListeners.push(cb)
    }

    private triggerOnPersonSelected(
        person?: PersonDto,
        personType?: PersonType,
    ) {
        for (const cb of this.onPersonSelectedListeners) {
            cb(person, personType)
        }
    }

    private triggerOnPersonHovered(
        person?: PersonDto,
        personType?: PersonType,
    ) {
        for (const cb of this.onPersonHoveredListeners) {
            cb(person, personType)
        }
    }

    private addOrMergeCompany(company: CompanyDto, parentPersonId: string) {
        if (!this.sdCompanies.has(company.id)) {
            this.sdCompanies.set(company.id, {
                id: company.id,
                company,
                renderMode: RenderModes.NORMAL,
                selected: false,
                parentPersonIds: new SetExtra([parentPersonId]),
            })
        } else {
            // merge with existing
            const sdCompany = this.sdCompanies.getOrThrow(company.id)
            sdCompany.parentPersonIds.add(parentPersonId)
        }
    }

    public initialiseSeedPersons(
        blueSeedPersons: PersonLiteDto[],
        networkSeedPersons: PersonLiteDto[],
    ) {
        for (const blueSeed of blueSeedPersons) {
            this.sdBluePeople.set(blueSeed.id, {
                id: blueSeed.id,
                person: blueSeed,
                renderMode: RenderModes.NORMAL,
                selected: false,
                personType: PersonType.HIRING_MANAGER,
            })
        }

        for (const blueSeed of networkSeedPersons) {
            this.sdBluePeople.set(blueSeed.id, {
                id: blueSeed.id,
                person: blueSeed,
                renderMode: RenderModes.NORMAL,
                selected: false,
                personType: PersonType.NETWORK_SEED,
            })
        }
    }

    // the passed in candidates will be ADDED to the existing ones
    //  if the candidate already exists they will be "merged" together i.e. if the new version of candidate contains a new connection
    //  to a blue/green person it will be added to the existing candidate
    public async addCandidates(candidates: CandidateLiteDto[]) {
        for (const candidate of candidates) {
            // do not allow blue/green persons to be added as candidates - they can't be both
            if (this.sdBluePeople.has(candidate.candidatePersonId)) {
                continue
            }

            let sdCandidate: SDCandidatePerson

            if (this.sdCandidates.has(candidate.candidatePersonId)) {
                sdCandidate = this.sdCandidates.getOrThrow(
                    candidate.candidatePersonId,
                )
                this.mergeCandidate(candidate, sdCandidate)
            } else {
                const conns: SDCandidateConnection[] = []

                for (const candidateConn of candidate.connections) {
                    for (const sharedWork of candidateConn.sharedWorkHistory ??
                        []) {
                        // shows each company/position this candidate worked at with this specific blue/green person
                        this.addOrMergeCompany(
                            await this.getCompany(sharedWork.companyId),
                            candidateConn.connectedPersonId,
                        )

                        conns.push({
                            parentCompanyId: sharedWork.companyId,
                            parentPersonId: candidateConn.connectedPersonId,
                        })
                    }
                }

                sdCandidate = {
                    id: candidate.candidatePersonId,
                    candidate,
                    renderMode: RenderModes.NORMAL,
                    selected: false,
                    connections: conns,
                }

                this.sdCandidates.set(candidate.candidatePersonId, sdCandidate)
            }
        }

        this.updateD3Data()
    }

    public candidateChanged(
        candidate: Pick<CandidateLiteDto, 'candidatePersonId'> &
            Partial<CandidateLiteDto>,
    ) {
        if (!this.sdCandidates.has(candidate.candidatePersonId)) {
            // the candidate doesn't exist in our list - nothing we can do
            return
            // throw new Error(
            //     `Candidate ${candidate.candidatePersonId} does not exist in the sdCandidates`,
            // )
        }

        // only update updatable fields on the candidate - for example "connections" is not updatable
        const sdCandidate = this.sdCandidates.getOrThrow(
            candidate.candidatePersonId,
        )
        sdCandidate.candidate.present = candidate.present

        this.simManager.updatePersonNode({
            id: candidate.candidatePersonId,
            statusInfo: this.showStatusInfo
                ? getCandidateStatusInfo(sdCandidate.candidate)
                : undefined,
        })
    }

    // private personType(personId: string): PersonType {
    //     if (this.sdBluePeople.has(personId)) return PersonType.BLUE
    //     if (this.sdGreenPeople.has(personId)) return PersonType.GREEN
    //     throw new Error(`Unknown person type for personId ${personId}`)
    // }

    // private getColourHexFromPersonType(personType: PersonType): string {
    //     switch (personType) {
    //         case PersonType.BLUE:
    //             return '#0246CE'
    //         // case PersonType.GREEN:
    //         //     return '#2ECC71'
    //         default:
    //             throw new Error(`Unknown personType: ${personType}`)
    //     }
    // }

    private updateD3Data() {
        const d3Nodes = this.buildD3Nodes()
        const d3Links = this.buildD3Links()

        this.simManager.updateData(d3Nodes, d3Links)
    }

    private buildD3Nodes(): D3Node[] {
        const d3Nodes: D3Node[] = []

        // create all the person nodes (candidates)
        for (const candidate of this.sdCandidates.getValuesAsArray()) {
            if (candidate.renderMode !== 'hidden') {
                const candidatePerson = this.personLiteCache.getOrThrow(
                    candidate.candidate.candidatePersonId,
                )

                d3Nodes.push({
                    id: candidate.candidate.candidatePersonId,
                    firstName: candidatePerson.firstName,
                    lastName: candidatePerson.lastName,
                    picUrl: getProfilePicUrl(candidatePerson, true),
                    type: 'person',
                    personType: PersonType.CANDIDATE,
                    statusInfo: this.showStatusInfo
                        ? getCandidateStatusInfo(candidate.candidate)
                        : undefined,
                })
            }
        }

        // create all the person nodes (seeds)
        for (const bluePerson of this.sdBluePeople.getValuesAsArray()) {
            if (bluePerson.renderMode !== 'hidden') {
                d3Nodes.push({
                    id: bluePerson.person.id,
                    firstName: bluePerson.person.firstName,
                    lastName: bluePerson.person.lastName,
                    picUrl: getProfilePicUrl(bluePerson.person, true),
                    type: 'person',
                    personType: bluePerson.personType,
                })
            }
        }

        // create all the company nodes
        for (const company of this.sdCompanies.getValuesAsArray()) {
            if (company.renderMode !== 'hidden') {
                const companyLogoUrl =
                    company.company.companyLogoUrl?.trim() || undefined
                d3Nodes.push({
                    id: company.company.id,
                    name: company.company.name,
                    companyLogoUrl,
                    type: 'company',
                })
            }
        }

        return d3Nodes
    }

    private buildD3Links(): D3Link[] {
        const linkKeys = new SetExtra<string>()
        const d3Links: D3Link[] = []

        // dump our cache of link properties
        this.sdLinkProps.clear()

        // create links between green people and blue people
        // for (const greenPerson of this.sdGreenPeople.getValuesAsArray()) {
        //     if (greenPerson.renderMode !== 'hidden') {
        //         for (const bluePersonId of greenPerson.parentBluePersonIds) {
        //             const linkKey = `${greenPerson.person.id}-${bluePersonId}`
        //             if (!linkKeys.has(linkKey)) {
        //                 linkKeys.add(linkKey)

        //                 const link: D3Link = {
        //                     source: bluePersonId,
        //                     target: greenPerson.person.id,
        //                     colour: '#0246CE', // blue
        //                 }
        //                 d3Links.push(link)
        //             }
        //         }
        //     }
        // }

        // now we need to do some tree walking

        // we need a link between the candidate and the company that they worked at with the blue/green person
        //  we need to know if the person worked with there was green or blue - if there are multiple, blue wins

        for (const candidate of this.sdCandidates.getValuesAsArray()) {
            // key: company id
            // const companyConnType = new MapExtra<string, PersonType>()

            // for (const conn of candidate.connections) {
            //     // for each company we need to know if the candidate has a blue connection or not
            //     //  if they have a blue connection then the link between the candidate and the company is blue
            //     //  otherwise it's green
            //     // if (!companyConnType.has(conn.parentCompanyId)) {
            //     //     companyConnType.set(
            //     //         conn.parentCompanyId,
            //     //         conn.parentPersonType,
            //     //     )
            //     // } else {
            //     //     // it exists in the map - if the new one is blue overwrite, otherwise leave it as is (either green or blue)
            //     //     if (conn.parentPersonType === PersonType.BLUE) {
            //     //         companyConnType.set(
            //     //             conn.parentCompanyId,
            //     //             PersonType.BLUE,
            //     //         )
            //     //     }
            //     // }

            //     // we also need to draw a link between the company and the blue/green person
            //     //  the colour will depend if the person is blue or green and we should de-dupe
            //     const linkKey = `${conn.parentPersonId}-${conn.parentCompanyId}`
            //     if (!linkKeys.has(linkKey)) {
            //         linkKeys.add(linkKey)

            //         d3Links.push({
            //             source: conn.parentPersonId, // the seed person is the parent of the company
            //             target: conn.parentCompanyId,
            //             colour: this.hexBlue,
            //         })
            //     }
            // }

            // now we know what colour each link should be between the candidate and the company
            //  we can loop round again creating the links
            for (const conn of candidate.connections) {
                // draw link between seed person and company
                const linkKey1 = `${conn.parentPersonId}-${conn.parentCompanyId}`
                if (!linkKeys.has(linkKey1)) {
                    linkKeys.add(linkKey1)

                    d3Links.push({
                        source: conn.parentPersonId, // the seed person is the parent of the company
                        target: conn.parentCompanyId,
                        colour: colors.gray[400],
                    })
                }

                // draw link between company and candidate
                const linkKey = `${conn.parentCompanyId}-${candidate.candidate.candidatePersonId}`
                if (!linkKeys.has(linkKey)) {
                    linkKeys.add(linkKey)

                    d3Links.push({
                        source: conn.parentCompanyId,
                        target: candidate.candidate.candidatePersonId,
                        colour: colors.gray[400],
                    })
                }
            }
        }

        // create our cache of link properties
        for (const d3Link of d3Links) {
            const d3LinkKey = getLinkKey(d3Link.source, d3Link.target)

            if (this.sdLinkProps.has(d3LinkKey)) {
                throw new Error(
                    `Attempting to create duplicate link id: ${d3LinkKey}`,
                )
            }

            this.sdLinkProps.set(d3LinkKey.toString(), {
                key: d3LinkKey,
                renderMode: RenderModes.NORMAL,
            })
        }

        return d3Links
    }

    private mergeCandidate(
        candidate: CandidateLiteDto,
        sdCandidate: SDCandidatePerson,
    ) {
        throw new Error(
            `mergeCandidate not implemented. candidate: ${candidate} sdCandidate: ${sdCandidate}`,
        )
    }
}

export enum PersonType {
    CANDIDATE = 'candidate',
    HIRING_MANAGER = 'hiring-manager',
    NETWORK_SEED = 'network-seed',
}

interface SDNodeBase {
    id: string
    // normal = default
    // disabled = greyed out
    // highlighted = bold border
    // hidden = removed from the D3 diagram altogether
    renderMode: RenderModes
    // only one of these should be set to true at any one time
    selected: boolean
}

// a "blue" person is a seed person
interface SDBluePerson extends SDNodeBase {
    person: PersonDto
    personType: PersonType
}

interface SDCandidatePerson extends SDNodeBase {
    // TODO: need to get the RAW job relevancy score from the API
    candidate: CandidateLiteDto
    // from this we can get a de-duplicated list of parent companies to draw the D3Links
    //  but as and when required we can work out exactly which blue/green people this candidate is linked to
    connections: SDCandidateConnection[]
}

interface SDCandidateConnection {
    parentCompanyId: string
    parentPersonId: string // seed person
}

interface SDLinkProps {
    key: string
    // normal = default
    // disabled = greyed out
    // highlighted = bolder
    // hidden = removed from the D3 diagram altogether
    renderMode: RenderModes
}

interface SDCompany extends SDNodeBase {
    company: CompanyDto
    parentPersonIds: SetExtra<string>
}

enum RenderModes {
    NORMAL = 'normal',
    DISABLED = 'disabled',
    HIGHLIGHTED = 'highlighted',
    HIDDEN = 'hidden',
}

enum NodeTypes {
    PERSON_BLUE = 'person-blue',
    PERSON_CANDIDATE = 'person-candidate',
    COMPANY = 'company',
}
